import _ from "lodash";

export default function VisualizationsService(
    $sbColor,
    $filter,
    $rootScope,
    $sbSketchVisualization,
    $sbSketchGenerator,
    $sbTemplate,
    $q,
    MODES,
    SABLONO_STATES
) {
    "ngInject";

    const STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE = [
        "DONE",
        "CONFIRMED",
    ];
    const BROWSE_ACTIVITY_STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE = [
        "STARTED",
        "DONE",
        "CONFIRMED",
    ];
    const PREDECESSOR_STATES_THAT_COUNT_AS_FINISHED = ["DONE", "CONFIRMED"];

    // States considered "stage reached" for a deliverable, matching findLatestStageReached
    // in svg.service.js: work-done (DONE, WFC, CONFIRMED) plus REJECTED.
    const STAGE_REACHED_STATES = [
        "DONE",
        "WAITING_FOR_CONFIRMATION",
        "CONFIRMED",
        "REJECTED",
    ];

    var stageColors = $sbColor.color.visualization.stages;

    var STAGE_COLORS = {
        notApplicable: stageColors.NOT_APPLICABLE,
        notStartedStroke: stageColors.NOT_APPLICABLE, // just to pick up a grey color, has nothing to do with the actual state
        notStarted: stageColors.NOT_STARTED,
        available: stageColors.STARTED_OR_AVAILABLE,
        started: stageColors.STARTED_OR_AVAILABLE,
        waitingForConfirmation:
            stageColors.COMPLETED_OR_WAITING_FOR_CONFIRMATION,
        completed: stageColors.COMPLETED_OR_WAITING_FOR_CONFIRMATION,
        rejected: stageColors.REJECTED,
    };

    var BROWSE_ACTIVITY_STATE_TO_COLOR = {
        NOT_STARTED: STAGE_COLORS.notStarted,
        AVAILABLE: STAGE_COLORS.available,
        STARTED: STAGE_COLORS.started,
        DONE: STAGE_COLORS.completed,
        CONFIRMED: STAGE_COLORS.completed,
        WAITING_FOR_CONFIRMATION: STAGE_COLORS.waitingForConfirmation,
        REJECTED: STAGE_COLORS.rejected,
    };

    // set default strategy
    //
    var strategyService = $sbSketchVisualization;

    var service = {
        determineStrategyByState: determineStrategyByState,
        loadVisualization: loadVisualization,
        fetchContentByMode: fetchContentByMode,
        setContentByMode: setContentByMode,
        updateColorPropertyForComponentsBy: updateColorPropertyForComponentsBy,
        resolveStructureColors: resolveStructureColors,
        filterLegendByVisibleColors: filterLegendByVisibleColors,
        STAGE_COLORS: STAGE_COLORS,
    };

    $rootScope.$on("$stateChangeSuccess", function (event, toState) {
        if (
            toState.name === SABLONO_STATES.visualizationSketch ||
            toState.name === SABLONO_STATES.visualizationGenerated
        ) {
            service.determineStrategyByState(toState.name);
        }
    });

    function determineStrategyByState(stateName) {
        switch (stateName) {
            case SABLONO_STATES.visualizationGenerated: {
                strategyService = $sbSketchGenerator;
                break;
            }
            case SABLONO_STATES.visualizationSketch: {
                strategyService = $sbSketchVisualization;
                break;
            }
            default: {
                var err = new Error("IllegalArgumentException");
                err.message =
                    "The " +
                    stateName +
                    " is not a valid argument. Only " +
                    SABLONO_STATES.visualizationGenerated +
                    " and " +
                    SABLONO_STATES.visualizationGenerated +
                    " are allowed.";
                throw err;
            }
        }
    }

    function loadVisualization(svgKey) {
        // delegate retrieval of SVG to chosen service
        //
        return strategyService.loadVisualization(svgKey);
    }

    /**
     * Gets all the content to be displayed in a given svg sketch, transforming and enriching
     * the data to get all necessary properties. It returns the components with color information
     * and the legend with all the colors and entries for a given mode.
     *
     * @param {string} svgId
     * @param {object} mode
     * @returns {Promise}
     */
    function fetchContentByMode(svgId, mode) {
        return strategyService
            .fetchContentByMode(svgId, mode.name)
            .then(function (deliverables) {
                return setContentByMode(deliverables, mode);
            });
    }

    /**
     * Transforming and enriching the data to get all necessary properties.
     * It returns the components with color information and the legend with all the colors and entries for a given mode.
     *
     * @param {Array<object>} deliverables
     * @param {object} mode
     * @return {*}
     */
    function setContentByMode(deliverables, mode) {
        if (deliverables && deliverables.length) {
            const coloredDeliverables = deliverables.map(
                _addColorsToDeliverable
            );

            return $sbTemplate.getTemplates().then((templates) => {
                const legend = _getLegendFromComponents(
                    coloredDeliverables,
                    mode,
                    templates
                );
                return {
                    components: coloredDeliverables,
                    legend: legend,
                };
            });
        } else {
            return $q.resolve({
                components: [],
                legend: [],
            });
        }
    }

    /**
     * Loop through all components to create a legend with all the colors that have at least
     * one occurrence in the components array.
     *
     * @param {array} components
     * @param {object} mode
     * @param {array} templates
     * @returns {*}
     * @private
     */
    function _getLegendFromComponents(components, mode, templates) {
        let legend;
        // special case for this mode
        //
        if (mode === MODES.BROWSE_ACTIVITY) {
            legend = _createLegendItemsForBrowseStatusMode(components);

            return removeDuplicateEntriesFrom(legend);
        } else {
            // all the other modes based on the components
            //
            legend = _createLegendItemsFrom(components, mode);
        }

        // On stage mode
        if (mode === MODES.STAGE) {
            return _sortStageModeEntriesAndRemoveDuplicates(legend, templates);
        }

        // Return the legend with unique entries and naturally sorted
        //
        return sortEntriesAndRemoveDuplicates(legend);
    }

    /**
     * Determine legend entries for components by currently active mode.
     *
     * @param components
     * @param mode
     * @returns {Array}
     * @private
     */
    function _createLegendItemsFrom(components, mode) {
        var legend = [
            {
                stateEntries: [],
            },
        ];

        components.forEach(function (component) {
            var newEntry = {};
            // If it's the default color, overwrite with the no value entry
            //
            if (_isDefaultColor(component[mode.colorProperty])) {
                newEntry = _createLegendEntryForNoValue(component, mode);
            } else {
                switch (mode) {
                    case MODES.STAGE:
                        newEntry = _createLegendEntryForStageMode(
                            component,
                            mode.colorProperty
                        );

                        // Dotted entries
                        //
                        if (_shouldDisplayOutOfSequencePattern(component)) {
                            legend[0].stateEntries.push(
                                _createLegendEntryForOutOfSequenceActivity(
                                    component
                                )
                            );
                        }

                        // Hatch entries
                        //
                        if (component.HATCH_COLOR) {
                            legend[0].stateEntries.push(
                                _createLegendEntryForHatchColor(component)
                            );
                        }

                        // Cross hatch entries
                        //
                        if (component.CROSS_HATCH_COLOR) {
                            legend[0].stateEntries.push(
                                _createLegendEntryForCrossHatchColor(component)
                            );
                        }
                        break;
                    //TODO [Feature-Flagged]  "deprecated-feature-to-remove-soon"
                    // case MODES.CURRENT:
                    //     newEntry = _createLegendEntryForCurrentMode(
                    //         component,
                    //         mode.colorProperty
                    //     );
                    //     break;
                    // case MODES.PROGRESS:
                    //     newEntry = _createLegendEntryForProgressMode(
                    //         component,
                    //         mode.colorProperty
                    //     );
                    //     break;
                }
            }

            // Push the entry into the legend
            //
            legend[0].stateEntries.push(newEntry);

            // Stroke entries
            //
            if (component.STROKE_COLOR) {
                legend[0].stateEntries.push(
                    _createLegendEntryForStrokeColor(component)
                );
            }
        });

        return legend;
    }

    /**
     * Create legend items for stage reached mode.
     *
     * @param components
     * @returns {*[]}
     * @private
     */
    function _createLegendItemsForBrowseStatusMode(components) {
        var legend = [
            {
                stateEntries: [
                    {
                        color: STAGE_COLORS.notApplicable,
                        key: "VISUALIZATION_LEGEND_ENTRY_STAGE_NOT_APPLICABLE",
                    },
                    {
                        color: STAGE_COLORS.notStarted,
                        overrideWhiteColor: STAGE_COLORS.notApplicable,
                        key: "VISUALIZATION_LEGEND_ENTRY_STAGE_NOT_STARTED",
                    },
                    {
                        color: STAGE_COLORS.available,
                        hatch: true,
                        key: "VISUALIZATION_LEGEND_ENTRY_STAGE_AVAILABLE",
                    },
                    {
                        color: STAGE_COLORS.started,
                        key: "VISUALIZATION_LEGEND_ENTRY_STAGE_STARTED",
                    },
                    {
                        color: STAGE_COLORS.waitingForConfirmation,
                        hatch: true,
                        key: "_WAITING_FOR_CONFIRMATION",
                    },
                    {
                        color: STAGE_COLORS.rejected,
                        crossHatch: true,
                        key: "_REJECTED",
                    },
                    {
                        color: STAGE_COLORS.completed,
                        key: "VISUALIZATION_LEGEND_ENTRY_STAGE_COMPLETED",
                    },
                    {
                        color: $sbColor.color.STAGE_PATTERN_COLOR,
                        outOfSequenceActivity: true,
                        key: "_OUT_OF_SEQUENCE_ACTIVITY",
                    },
                ],
            },
        ];

        components.forEach(function (component) {
            if (component.STROKE_COLOR) {
                legend[0].stateEntries.push(
                    _createLegendEntryForStrokeColor(component)
                );
            }
        });

        return legend;
    }

    function _createLegendEntryForStageMode(component, colorProperty) {
        const newEntry = {
            color: component[colorProperty],
            templateId: component.TEMPLATE_ID,
        };

        if (component.STAGE) {
            newEntry.topologicalIndex = component.STAGE.TOPOLOGICAL_INDEX + "";
            newEntry.key = component.STAGE.STATE_NAME;
        }

        return newEntry;
    }

    //TODO [Feature-Flagged]  "deprecated-feature-to-remove-soon"
    // function _createLegendEntryForCurrentMode(component, colorProperty) {
    //     var newEntry = {
    //         color: component[colorProperty],
    //     };
    //     if (component.CURRENT) {
    //         newEntry.key = component.CURRENT.TEAM_NAME;
    //     }
    //     return newEntry;
    // }
    // function _createLegendEntryForProgressMode(component, colorProperty) {
    //     return {
    //         color: component[colorProperty],
    //         key: $sbColor.getLegendKeyFromColor(component[colorProperty]),
    //     };
    // }

    function _createLegendEntryForNoValue(component, mode) {
        return {
            color: STAGE_COLORS.notStartedStroke,
            stroke: true,
            key: mode.noValueKey,
        };
    }

    function _createLegendEntryForStrokeColor(component) {
        return {
            color: component.STROKE_COLOR,
            stroke: true,
            key: $sbColor.getLegendKeyFromColor(component.STROKE_COLOR),
        };
    }

    function _createLegendEntryForHatchColor() {
        return {
            color: $sbColor.color.STAGE_PATTERN_COLOR,
            hatch: true,
            key: "_WAITING_FOR_CONFIRMATION",
        };
    }

    function _createLegendEntryForOutOfSequenceActivity() {
        return {
            color: $sbColor.color.STAGE_PATTERN_COLOR,
            outOfSequenceActivity: true,
            key: "_OUT_OF_SEQUENCE_ACTIVITY",
        };
    }

    function _createLegendEntryForCrossHatchColor() {
        return {
            color: $sbColor.color.STAGE_PATTERN_COLOR,
            crossHatch: true,
            key: "_REJECTED",
        };
    }

    function _shouldDisplayOutOfSequencePattern(component) {
        return _isOutOfSequence(component) && _isDoneOrConfirmed(component);
    }

    function _isOutOfSequence(component) {
        return !!component.HAS_OUT_OF_SEQUENCE_ACTIVITY;
    }

    function _isDoneOrConfirmed(component) {
        return _.includes(
            STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE,
            _.get(component, "STAGE.STATE")
        );
    }

    /**
     * Determines whether a color is the default color
     * @param color
     * @returns {boolean}
     * @private
     */
    function _isDefaultColor(color) {
        return color === STAGE_COLORS.notStarted;
    }

    /**
     * Adds color properties to a given deliverable
     *
     * @param {Object} deliverable
     * @returns {Object} component - with updated properties
     * @private
     */
    function _addColorsToDeliverable(deliverable) {
        // Remove hatch and stroke styles applied from the last selected mode
        //
        deliverable = _cleanStrokeAndHatchColorsFrom(deliverable);

        // All available modes
        //
        //TODO [Feature-Flagged]  "deprecated-feature-to-remove-soon"
        // deliverable[MODES.PROGRESS.colorProperty] = $sbColor.color.fromStatus(
        //     deliverable.IS_BEHIND
        // );

        if (deliverable.STAGE) {
            deliverable[MODES.STAGE.colorProperty] =
                $sbColor.toHexColor(deliverable.STAGE.COLOR) ||
                STAGE_COLORS.notStarted;
        } else {
            deliverable[MODES.STAGE.colorProperty] = STAGE_COLORS.notStarted;
        }

        //TODO [Feature-Flagged]  "deprecated-feature-to-remove-soon"
        // if (deliverable.HAS_PROGRESS_REPORTED && deliverable.CURRENT) {
        //     deliverable[MODES.CURRENT.colorProperty] =
        //         $sbColor.toHexColor(deliverable.CURRENT.TEAM_COLOR) ||
        //         STAGE_COLORS.notStarted;
        // } else {
        //     deliverable[MODES.CURRENT.colorProperty] = STAGE_COLORS.notStarted;
        // }

        // Stroke colors
        //
        if (deliverable.CLAIM_QUANTITY) {
            deliverable.STROKE_COLOR = $sbColor.color.outline.withClaims;
        }
        if (deliverable.OBSTRUCTION_QUANTITY) {
            deliverable.STROKE_COLOR = $sbColor.color.outline.withObstructions;
        }

        // Requires HATCH pattern for confirmation
        //
        if (
            deliverable.STAGE &&
            deliverable.STAGE.STATE === MODES.STAGE.requiresConfirmation
        ) {
            deliverable.HATCH_COLOR = $sbColor.color.WAITING_FOR_CONFIRMATION;
        }

        // Requires HATCH pattern for confirmation
        //
        if (
            deliverable.STAGE &&
            deliverable.STAGE.STATE === MODES.STAGE.rejected
        ) {
            deliverable.CROSS_HATCH_COLOR = $sbColor.color.behindSchedule;
        }

        return deliverable;
    }

    function _cleanStrokeAndHatchColorsFrom(component) {
        return _clearParamsFrom(component, [
            "STROKE_COLOR",
            "HATCH_COLOR",
            "CROSS_HATCH_COLOR",
            "SELECTED_ACTIVITY_STATE",
        ]);
    }

    /**
     * Delete all provided parameters from the given object.
     *
     * @param {Object} object
     * @param  {string[]} params
     * @returns {Object}
     * @private
     */
    function _clearParamsFrom(object, params) {
        return _.pick(object, _.difference(_.keys(object), params));
    }

    function updateColorPropertyForComponentsBy(stageId, components) {
        return components.map(function setColorsOn(component) {
            var activity = _.find(component.ACTIVITIES, [
                "TEMPLATE_ID",
                stageId,
            ]);

            _setComponentColorsBasedOnActivityState(component, activity);

            return component;
        });
    }

    function _setComponentColorsBasedOnActivityState(component, activity) {
        var colorProperty = MODES.BROWSE_ACTIVITY.colorProperty;

        if (activity) {
            component[colorProperty] =
                BROWSE_ACTIVITY_STATE_TO_COLOR[activity.STATE];
            component.SELECTED_ACTIVITY_STATE = activity.STATE;

            // Stroke colors (issues/obstructions) - based on activity numbers
            //
            component.STROKE_COLOR = null;
            if (activity.CLAIM_QUANTITY) {
                component.STROKE_COLOR = $sbColor.color.outline.withClaims;
            }
            if (activity.OBSTRUCTION_QUANTITY) {
                component.STROKE_COLOR =
                    $sbColor.color.outline.withObstructions;
            }

            _setHatchColorsBasedOnActivityState(
                component,
                colorProperty,
                activity.STATE
            );

            _setOutOfSequenceStateForBrowseActivity(component, activity);
        } else {
            component[colorProperty] = STAGE_COLORS.notApplicable;
            component.SELECTED_ACTIVITY_STATE = null;
            component.STROKE_COLOR = null;
            _unsetHatchColors(component);
            component.HAS_OUT_OF_SEQUENCE_ACTIVITY_ON_SELECTED_ACTIVITY = false;
        }

        return component;
    }

    function _setHatchColorsBasedOnActivityState(
        component,
        colorProperty,
        activityState
    ) {
        switch (activityState) {
            case "WAITING_FOR_CONFIRMATION": // fall through
            case "AVAILABLE":
                component.HATCH_COLOR = component[colorProperty];
                component.CROSS_HATCH_COLOR = null;
                break;
            case "REJECTED":
                component.HATCH_COLOR = null;
                component.CROSS_HATCH_COLOR = component[colorProperty];
                break;
            default:
                _unsetHatchColors(component);
        }
    }

    function _unsetHatchColors(component) {
        component.HATCH_COLOR = null;
        component.CROSS_HATCH_COLOR = null;
    }

    function _setOutOfSequenceStateForBrowseActivity(component, activity) {
        component.HAS_OUT_OF_SEQUENCE_ACTIVITY_ON_SELECTED_ACTIVITY =
            _hasUnfinishedPredecessorOnSelectedActivity(component, activity);
    }

    function _hasUnfinishedPredecessorOnSelectedActivity(component, activity) {
        if (!_isSelectedBrowseActivityStateEligibleForOutOfSequence(activity)) {
            return false;
        }

        const selectedTopologicalIndex = Number(
            _.get(activity, "TOPOLOGICAL_INDEX")
        );

        if (!Number.isFinite(selectedTopologicalIndex)) {
            return false;
        }

        return _.some(component.ACTIVITIES || [], function (predecessor) {
            const predecessorTopologicalIndex = Number(
                _.get(predecessor, "TOPOLOGICAL_INDEX")
            );

            return (
                Number.isFinite(predecessorTopologicalIndex) &&
                predecessorTopologicalIndex < selectedTopologicalIndex &&
                !_isPredecessorFinishedOrConfirmed(predecessor)
            );
        });
    }

    function _isSelectedBrowseActivityStateEligibleForOutOfSequence(activity) {
        return _.includes(
            BROWSE_ACTIVITY_STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE,
            _.get(activity, "STATE")
        );
    }

    function _isPredecessorFinishedOrConfirmed(activity) {
        return _.includes(
            PREDECESSOR_STATES_THAT_COUNT_AS_FINISHED,
            _.get(activity, "STATE")
        );
    }

    function sortEntriesAndRemoveDuplicates(legend) {
        legend = removeDuplicateEntriesFrom(legend);
        const sortedStateEntries = $filter("naturalSort")(
            legend[0].stateEntries,
            "key"
        );
        return [{ stateEntries: sortedStateEntries }];
    }

    function _sortStageModeEntriesAndRemoveDuplicates(legend, templates) {
        legend = removeDuplicateEntriesFrom(legend);

        const legendWithHeaders = _addStageModeHeadersToLegend(
            legend,
            templates
        );

        const stateEntries = $filter("naturalSort")(
            legendWithHeaders[0].stateEntries,
            "templateId",
            "topologicalIndex"
        );

        return [
            {
                stateEntries,
            },
        ];
    }

    function _addStageModeHeadersToLegend(legend, templates) {
        const MINIMUM_TOPOLOGICAL_INDEX = "0.5";
        const uniqueTemplateIDs = _findUniqueTemplateIDs(
            legend[0].stateEntries
        );

        // When one template is present do not display template name in legend
        if (uniqueTemplateIDs.length <= 1) {
            return legend;
        }

        for (let templateId of uniqueTemplateIDs) {
            let template = getTemplate(templateId, templates);
            legend[0].stateEntries.push({
                templateId: templateId,
                key: template.NAME,
                topologicalIndex: MINIMUM_TOPOLOGICAL_INDEX,
                header: true,
            });
        }

        legend[0].stateEntries.push({
            key: "LEGEND_STAGE_MODE_DEFAULTS",
            header: true,
            topologicalIndex: MINIMUM_TOPOLOGICAL_INDEX,
        });

        return legend;
    }

    function getTemplate(templateId, templates) {
        return _.find(templates, { ID: templateId });
    }

    function _findUniqueTemplateIDs(legend) {
        const templateIds = legend
            .map(({ templateId }) => templateId)
            .filter(_.isString);
        return _.without(_.uniq(templateIds), undefined);
    }

    function removeDuplicateEntriesFrom(legend) {
        const sortedStateEntries = _.uniqWith(
            legend[0].stateEntries,
            _.isEqual
        );

        return [{ stateEntries: sortedStateEntries }];
    }

    /**
     * Resolves a fill style for each structure element in the SVG based on the
     * deliverables that belong to it and the currently active visualization mode.
     *
     * Structure elements in the SVG carry `data-sb-type="structure"` and their DOM
     * id is expected to match the deliverable's STRUCTURE_ID.
     *
     * @param {Object[]} coloredDeliverables - Deliverables already enriched with color
     *   properties (output of setContentByMode / _addColorsToDeliverable). Each entry
     *   must have a STRUCTURE_ID property populated from the API response.
     * @param {Object} mode - The active visualization mode (e.g. MODES.STAGE).
     * @param {boolean} markOutOfSequence - Whether the out-of-sequence feature toggle is enabled.
     * @returns {Map<string, {color: string, hatch: boolean, crossHatch: boolean, outOfSequence: boolean, strokeColor: string|null}>}
     *   Map from STRUCTURE_ID to a resolved style object.
     */
    function resolveStructureColors(
        coloredDeliverables,
        mode,
        markOutOfSequence
    ) {
        const deliverablesWithStructure = coloredDeliverables.filter(
            (deliverable) => deliverable.STRUCTURE_ID
        );
        const deliverablesByStructure = _.groupBy(
            deliverablesWithStructure,
            "STRUCTURE_ID"
        );

        return new Map(
            Object.entries(deliverablesByStructure).map(
                ([structureId, structureDeliverables]) => {
                    return [
                        structureId,
                        _resolveColorForStructure(
                            structureDeliverables,
                            mode,
                            markOutOfSequence
                        ),
                    ];
                }
            )
        );
    }

    function filterLegendByVisibleColors(legend, visibleColors) {
        const visibleColorSet = new Set(visibleColors);

        return legend.map((entry) => {
            if (!entry.stateEntries) {
                return entry;
            }

            return Object.assign({}, entry, {
                stateEntries: entry.stateEntries.filter(
                    (legendEntry) =>
                        legendEntry.header ||
                        legendEntry.key === MODES.STAGE.noValueKey ||
                        legendEntry.hatch ||
                        legendEntry.crossHatch ||
                        legendEntry.outOfSequenceActivity ||
                        visibleColorSet.has(legendEntry.color)
                ),
            });
        });
    }

    /**
     * Derives a single representative style for a structure from its deliverables.
     *
     * Show Status mode: finds the stage-reached activity (DONE, CONFIRMED,
     * WAITING_FOR_CONFIRMATION, or REJECTED) with the latest baseline end date (CD)
     * across all deliverables, and uses its color. TOPOLOGICAL_INDEX is used as a
     * tie-breaker for equal CD dates, and as the primary sort key when no CD is present.
     * This allows comparing activities from different workflows by calendar date
     * rather than topological index.
     *
     * Browse Activity mode: aggregates the SELECTED_ACTIVITY_STATE set by
     * updateColorPropertyForComponentsBy using a fixed priority order so that the
     * most attention-needing state always wins:
     *   Rejected > Waiting for Confirmation > Started > Available > Done > Confirmed > Not Started
     *
     * @param {Object[]} structureDeliverables
     * @param {Object} mode
     * @param {boolean} markOutOfSequence
     * @returns {{color: string, hatch: boolean, crossHatch: boolean, outOfSequence: boolean, strokeColor: string|null}}
     */
    function _resolveColorForStructure(
        structureDeliverables,
        mode,
        markOutOfSequence
    ) {
        if (mode === MODES.BROWSE_ACTIVITY) {
            return _resolveColorForStructureInBrowseActivityMode(
                structureDeliverables,
                markOutOfSequence
            );
        }
        return _resolveColorForStructureInStageMode(
            structureDeliverables,
            markOutOfSequence
        );
    }

    function _resolveColorForStructureInStageMode(
        structureDeliverables,
        markOutOfSequence
    ) {
        // Collect every stage-reached activity across all deliverables in the
        // structure, mirroring what findLatestStageReached does per deliverable.
        const stageReachedActivities = [];
        for (const deliverable of structureDeliverables) {
            for (const activity of deliverable.ACTIVITIES || []) {
                if (_.includes(STAGE_REACHED_STATES, activity.STATE)) {
                    stageReachedActivities.push(activity);
                }
            }
        }

        if (!stageReachedActivities.length) {
            return {
                color: STAGE_COLORS.notStarted,
                strokeColor: _computeStructureStrokeColor(
                    structureDeliverables
                ),
                hatch: false,
                crossHatch: false,
                outOfSequence: false,
            };
        }

        const winner = _.chain(stageReachedActivities)
            .orderBy(
                ["CD", "TOPOLOGICAL_INDEX", "STATE_CHANGED_AT"],
                ["desc", "desc", "desc"]
            )
            .head()
            .value();

        const color =
            $sbColor.toHexColor(winner.COLOR) || STAGE_COLORS.notStarted;
        const hatch = winner.STATE === "WAITING_FOR_CONFIRMATION";
        const crossHatch = winner.STATE === "REJECTED";
        const outOfSequence =
            markOutOfSequence &&
            structureDeliverables.some(
                (d) =>
                    !!d.HAS_OUT_OF_SEQUENCE_ACTIVITY &&
                    _.includes(
                        STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE,
                        _.get(d, "STAGE.STATE")
                    )
            );

        return {
            color,
            strokeColor: _computeStructureStrokeColor(structureDeliverables),
            hatch,
            crossHatch,
            outOfSequence,
        };
    }

    function _resolveColorForStructureInBrowseActivityMode(
        structureDeliverables,
        markOutOfSequence
    ) {
        const FINISHED_STATES = ["DONE", "CONFIRMED"];
        const UNFINISHED_STATE_PRIORITY = [
            "REJECTED",
            "WAITING_FOR_CONFIRMATION",
            "STARTED",
            "AVAILABLE",
            "NOT_STARTED",
        ];

        // Only consider deliverables that have the selected activity (exclude not-applicable nulls)
        const deliverablesWithState = structureDeliverables.filter(
            (deliverable) =>
                deliverable.SELECTED_ACTIVITY_STATE !== null &&
                deliverable.SELECTED_ACTIVITY_STATE !== undefined
        );

        let winningState = null;

        if (deliverablesWithState.length > 0) {
            const allFinished = deliverablesWithState.every((deliverable) =>
                _.includes(FINISHED_STATES, deliverable.SELECTED_ACTIVITY_STATE)
            );

            if (allFinished) {
                // Structure is only green once every activity in it is done/confirmed
                winningState = deliverablesWithState.some(
                    (deliverable) =>
                        deliverable.SELECTED_ACTIVITY_STATE === "DONE"
                )
                    ? "DONE"
                    : "CONFIRMED";
            } else {
                // Some activities are not finished: use the highest-priority unfinished state
                for (const state of UNFINISHED_STATE_PRIORITY) {
                    if (
                        deliverablesWithState.some(
                            (deliverable) =>
                                deliverable.SELECTED_ACTIVITY_STATE === state
                        )
                    ) {
                        winningState = state;
                        break;
                    }
                }

                // Special case: when the only unfinished activities are NOT_STARTED but
                // some activities are already finished, the structure has been partially
                // started — promote to STARTED rather than NOT_STARTED
                if (
                    winningState === "NOT_STARTED" &&
                    deliverablesWithState.some((deliverable) =>
                        _.includes(
                            FINISHED_STATES,
                            deliverable.SELECTED_ACTIVITY_STATE
                        )
                    )
                ) {
                    winningState = "STARTED";
                }
            }
        }

        const color = winningState
            ? BROWSE_ACTIVITY_STATE_TO_COLOR[winningState]
            : STAGE_COLORS.notApplicable;
        const hatch =
            winningState === "AVAILABLE" ||
            winningState === "WAITING_FOR_CONFIRMATION";
        const crossHatch = winningState === "REJECTED";
        const outOfSequence =
            markOutOfSequence &&
            structureDeliverables.some(
                (deliverable) =>
                    _.includes(
                        BROWSE_ACTIVITY_STATES_THAT_CAN_BE_COMBINED_WITH_OUT_OF_SEQUENCE,
                        deliverable.SELECTED_ACTIVITY_STATE
                    ) &&
                    !!deliverable.HAS_OUT_OF_SEQUENCE_ACTIVITY_ON_SELECTED_ACTIVITY
            );

        return {
            color,
            strokeColor: _computeStructureStrokeColor(structureDeliverables),
            hatch,
            crossHatch,
            outOfSequence,
        };
    }

    function _computeStructureStrokeColor(structureDeliverables) {
        const obstructionColor = $sbColor.color.outline.withObstructions;
        const claimColor = $sbColor.color.outline.withClaims;
        if (
            structureDeliverables.some(
                (deliverable) => deliverable.STROKE_COLOR === obstructionColor
            )
        ) {
            return obstructionColor;
        }
        if (
            structureDeliverables.some(
                (deliverable) => deliverable.STROKE_COLOR === claimColor
            )
        ) {
            return claimColor;
        }
        return null;
    }

    return service;
}
